import { initAudioPlayerToggle, showPlayPauseIcon, hideAudioPlayerToggle } from './toggle.js';
import {
	getAudioPlayerElement, 
	getAudioPlayerCollapsed, 
	setAudioPlayerCollapsed,
	getCurrentAudioFile,
	AUDIO_PLAYER_WIDTH,
	AUDIO_PLAYER_HEIGHT,
	fadeInVolume,
	getSavedPlayerVolume,
	setSavedPlayerVolume,
	hideAudioPlayer
} from './player-core.js';
import { initPlayerStatePersistence, cleanupPlayerStatePersistence, loadSavedState, clearSavedState } from './player-state.js';

export function setupMediaSession(audioEl, fileName, filePath) {
	if (!navigator.mediaSession) {
		return; // Media Session API not supported
	}
	
	// Set metadata
	navigator.mediaSession.metadata = new MediaMetadata({
		title: fileName,
		artist: 'Library Audio',
		album: filePath || '',
		artwork: [
			// You can add artwork URLs here if available
			// { src: '/static/assets/images/audio-icon.png', sizes: '96x96', type: 'image/png' }
		]
	});
	
	// Set action handlers
	navigator.mediaSession.setActionHandler('play', function() {
		if (audioEl && audioEl.paused) {
			audioEl.play().catch(err => {
				console.warn('[AudioPlayer] Media Session play failed:', err);
			});
			// Visual feedback will be triggered by 'play' event listener
		}
	});
	
	navigator.mediaSession.setActionHandler('pause', function() {
		if (audioEl && !audioEl.paused) {
			audioEl.pause();
			// Visual feedback will be triggered by 'pause' event listener
		}
	});
	
	navigator.mediaSession.setActionHandler('seekbackward', function(details) {
		if (audioEl && audioEl.duration) {
			const seekTime = details.seekOffset || 10;
			audioEl.currentTime = Math.max(0, audioEl.currentTime - seekTime);
			// Trigger visual feedback
			const toggleButton = $('#audio-player-toggle');
			if (toggleButton.length) {
				toggleButton.addClass('audio-player-toggle--media-action');
				setTimeout(() => toggleButton.removeClass('audio-player-toggle--media-action'), 500);
			}
		}
	});
	
	navigator.mediaSession.setActionHandler('seekforward', function(details) {
		if (audioEl && audioEl.duration) {
			const seekTime = details.seekOffset || 10;
			audioEl.currentTime = Math.min(audioEl.duration, audioEl.currentTime + seekTime);
			// Trigger visual feedback
			const toggleButton = $('#audio-player-toggle');
			if (toggleButton.length) {
				toggleButton.addClass('audio-player-toggle--media-action');
				setTimeout(() => toggleButton.removeClass('audio-player-toggle--media-action'), 500);
			}
		}
	});
	
	// Map previous/next track buttons to seek backward/forward (15 seconds)
	navigator.mediaSession.setActionHandler('previoustrack', function() {
		if (audioEl && audioEl.duration) {
			audioEl.currentTime = Math.max(0, audioEl.currentTime - 15);
			// Trigger visual feedback
			const toggleButton = $('#audio-player-toggle');
			if (toggleButton.length) {
				toggleButton.addClass('audio-player-toggle--media-action');
				setTimeout(() => toggleButton.removeClass('audio-player-toggle--media-action'), 500);
			}
		}
	});
	
	navigator.mediaSession.setActionHandler('nexttrack', function() {
		if (audioEl && audioEl.duration) {
			audioEl.currentTime = Math.min(audioEl.duration, audioEl.currentTime + 15);
			// Trigger visual feedback
			const toggleButton = $('#audio-player-toggle');
			if (toggleButton.length) {
				toggleButton.addClass('audio-player-toggle--media-action');
				setTimeout(() => toggleButton.removeClass('audio-player-toggle--media-action'), 500);
			}
		}
	});
	
	// Update playback state when audio state changes
	const updatePlaybackState = () => {
		if (navigator.mediaSession) {
			navigator.mediaSession.playbackState = audioEl.paused ? 'paused' : 'playing';
		}
	};
	
	audioEl.addEventListener('play', updatePlaybackState);
	audioEl.addEventListener('pause', updatePlaybackState);
	audioEl.addEventListener('ended', function() {
		if (navigator.mediaSession) {
			navigator.mediaSession.playbackState = 'none';
		}
	});
}

export function clearMediaSession() {
	if (navigator.mediaSession) {
		navigator.mediaSession.metadata = null;
		navigator.mediaSession.setActionHandler('play', null);
		navigator.mediaSession.setActionHandler('pause', null);
		navigator.mediaSession.setActionHandler('seekbackward', null);
		navigator.mediaSession.setActionHandler('seekforward', null);
		navigator.mediaSession.setActionHandler('previoustrack', null);
		navigator.mediaSession.setActionHandler('nexttrack', null);
	}
}

export function animatePlayerCollapse(collapse, updateToggleButtonCallback) {
	const audioPlayerElement = getAudioPlayerElement();
	if (!audioPlayerElement || !audioPlayerElement.length) return;
	
	const toggleButton = $('#audio-player-toggle');
	if (!toggleButton.length) return;
	
	const playerRect = audioPlayerElement[0].getBoundingClientRect();
	const buttonRect = toggleButton[0].getBoundingClientRect();
	
	// Calculate center points
	const playerCenterX = playerRect.left + playerRect.width / 2;
	const playerCenterY = playerRect.top + playerRect.height / 2;
	const buttonCenterX = buttonRect.left + buttonRect.width / 2;
	const buttonCenterY = buttonRect.top + buttonRect.height / 2;
	
	// Calculate transform origin relative to player center
	const deltaX = buttonCenterX - playerCenterX;
	const deltaY = buttonCenterY - playerCenterY;
	
	// Convert to percentage for transform-origin
	const originX = 50 + (deltaX / playerRect.width) * 100;
	const originY = 50 + (deltaY / playerRect.height) * 100;
	
	const container = audioPlayerElement.find('.audio-player-container');
	
	if (collapse) {
		// Set collapsed state FIRST to prevent observer from re-showing player
		setAudioPlayerCollapsed(true);
		
		// Temporarily disconnect observer to prevent flickering
		if (updateToggleButtonCallback && updateToggleButtonCallback.disconnectObserver) {
			updateToggleButtonCallback.disconnectObserver();
		}
		
		// Collapse: scale down and move towards icon
		container.css({
			'transform-origin': `${originX}% ${originY}%`,
			'transition': 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
		});
		
		// Override overlay transition to prevent flickering
		audioPlayerElement.css({
			'transition': 'none',
			'opacity': '1' // Ensure opacity is 1 before animation starts
		});
		
		// Calculate movement distance
		const moveX = (buttonCenterX - playerCenterX) * 0.9; // 90% of distance
		const moveY = (buttonCenterY - playerCenterY) * 0.9;
		
		// Start animation
		setTimeout(() => {
			container.css({
				'transform': `scale(0.1) translate(${moveX}px, ${moveY}px)`,
				'opacity': '0'
			});
		}, 10);
		
		// Hide player after animation completes
		setTimeout(() => {
			// Remove class FIRST before cleaning up styles
			audioPlayerElement.removeClass('audio-player-overlay--visible');
			
			// Immediately hide element to prevent any CSS transitions from showing it
			audioPlayerElement.css({
				'display': 'none',
				'transition': 'none',
				'opacity': '0'
			});
			
			container.css({
				'transform': '',
				'opacity': '',
				'transform-origin': '',
				'transition': ''
			});
			
			// Restore display after a brief moment to allow cleanup
			setTimeout(() => {
				audioPlayerElement.css({
					'display': '',
					'transition': '',
					'opacity': ''
				});
			}, 50);
			
			toggleButton.addClass('audio-player-toggle--visible');
			toggleButton.addClass('active');
			
			// Reconnect observer after a delay to ensure all CSS transitions are complete
			setTimeout(() => {
				if (updateToggleButtonCallback && updateToggleButtonCallback.reconnectObserver) {
					updateToggleButtonCallback.reconnectObserver();
				}
			}, 100); // Additional delay to prevent flickering
		}, 500);
	} else {
		// Expand: start from icon position and scale up
		setAudioPlayerCollapsed(false);
		
		// Calculate movement distance (reverse)
		const moveX = (buttonCenterX - playerCenterX) * 0.9;
		const moveY = (buttonCenterY - playerCenterY) * 0.9;
		
		// First, make player visible but with zero opacity and scale
		audioPlayerElement.addClass('audio-player-overlay--visible');
		toggleButton.removeClass('active');
		
		// Override default transition temporarily
		audioPlayerElement.css({
			'transition': 'none',
			'opacity': '0'
		});
		
		container.css({
			'transform-origin': `${originX}% ${originY}%`,
			'transform': `scale(0.1) translate(${moveX}px, ${moveY}px)`,
			'opacity': '0',
			'transition': 'none'
		});
		
		// Force reflow to ensure initial state is applied
		container[0].offsetHeight;
		
		// Now apply transition and animate expansion
		setTimeout(() => {
			audioPlayerElement.css({
				'transition': '',
				'opacity': ''
			});
			
			container.css({
				'transition': 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
				'transform': 'scale(1) translate(0, 0)',
				'opacity': '1'
			});
		}, 10);
		
		// Clean up after animation
		setTimeout(() => {
			container.css({
				'transform': '',
				'opacity': '',
				'transform-origin': '',
				'transition': ''
			});
			audioPlayerElement.css({
				'transition': '',
				'opacity': ''
			});
		}, 500);
	}
}

export function setupPlayerUI(audioPlayerElement, fileName, filePath, audioUrl, savePositionCallback) {
	
	// Close handler
	const closePlayer = () => {
		// Stop audio playback before closing (for browser compatibility)
		const audio = audioPlayerElement.find('audio');
		if (audio.length > 0 && audio[0]) {
			const audioEl = audio[0];
			if (!audioEl.paused) {
				audioEl.pause();
			}
			// Reset playback position to ensure clean stop
			audioEl.currentTime = 0;
		}
		// Use centralized hideAudioPlayer function to ensure all state is cleaned up properly
		hideAudioPlayer();
		// Clear saved state (cookies and DB) when player is closed
		clearSavedState();
	};
	
	audioPlayerElement.find('.audio-player__close').on('click', closePlayer);
	
	// Minimize handler - hide player and show icon in offcanvas
	audioPlayerElement.find('.audio-player__minimize').on('click', function(e) {
		e.preventDefault();
		e.stopPropagation();
		animatePlayerCollapse(true, null);
	});
	
	// Drag functionality
	const header = audioPlayerElement.find('.audio-player__header');
	let isDragging = false;
	let dragStartX = 0;
	let dragStartY = 0;
	let playerStartLeft = 0;
	let playerStartTop = 0;
	
	header.on('mousedown', function(e) {
		if ($(e.target).closest('.audio-player__close').length || $(e.target).closest('.audio-player__minimize').length) {
			return;
		}
		isDragging = true;
		const playerRect = audioPlayerElement[0].getBoundingClientRect();
		dragStartX = e.clientX;
		dragStartY = e.clientY;
		playerStartLeft = playerRect.left;
		playerStartTop = playerRect.top;
		e.preventDefault();
		audioPlayerElement.addClass('audio-player--dragging');
	});
	
	$(document).on('mousemove.audio-player-drag', function(e) {
		if (!isDragging || !audioPlayerElement || !audioPlayerElement.length) return;
		const deltaX = e.clientX - dragStartX;
		const deltaY = e.clientY - dragStartY;
		const newLeft = playerStartLeft + deltaX;
		const newTop = playerStartTop + deltaY;
		const maxLeft = window.innerWidth - AUDIO_PLAYER_WIDTH;
		const maxTop = window.innerHeight - AUDIO_PLAYER_HEIGHT;
		const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
		const constrainedTop = Math.max(0, Math.min(newTop, maxTop));
		audioPlayerElement.css({
			left: constrainedLeft + 'px',
			top: constrainedTop + 'px'
		});
		savePositionCallback(constrainedLeft, constrainedTop);
	});
	
	$(document).on('mouseup.audio-player-drag', function() {
		if (isDragging) {
			isDragging = false;
			audioPlayerElement.removeClass('audio-player--dragging');
		}
	});
	
		// Initialize toggle button if not already initialized
		initAudioPlayerToggle();
		
		// Initialize state persistence (will be set up when audio element is ready)
	
	// Add event handlers for audio element
	const audio = audioPlayerElement.find('audio');
	if (audio.length > 0) {
		const audioEl = audio[0];
		
		// Setup Media Session API for system media controls
		setupMediaSession(audioEl, fileName, filePath);
		
		// Visual feedback for media key actions
		const toggleButton = $('#audio-player-toggle');
		let feedbackTimeout = null;
		
		const showMediaActionFeedback = () => {
			if (toggleButton.length) {
				// Clear existing timeout
				if (feedbackTimeout) {
					clearTimeout(feedbackTimeout);
					feedbackTimeout = null;
				}
				
				// Add feedback class
				toggleButton.addClass('audio-player-toggle--media-action');
				
				// Remove feedback class after animation
				feedbackTimeout = setTimeout(() => {
					toggleButton.removeClass('audio-player-toggle--media-action');
					feedbackTimeout = null;
				}, 500);
			}
		};
		
		// Listen for play/pause events
		audioEl.addEventListener('play', function() {
			showMediaActionFeedback();
			showPlayPauseIcon('play', toggleButton);
			// Dispatch custom event for toggle button update
			window.dispatchEvent(new CustomEvent('audioPlayerPlay'));
		});
		audioEl.addEventListener('pause', function() {
			showMediaActionFeedback();
			showPlayPauseIcon('pause', toggleButton);
			// Dispatch custom event for toggle button update
			window.dispatchEvent(new CustomEvent('audioPlayerPause'));
		});
		audioEl.addEventListener('ended', function() {
			console.log('[AudioPlayer] ⏹️ ENDED:', {
				currentTime: audioEl.currentTime.toFixed(2),
				duration: audioEl.duration ? audioEl.duration.toFixed(2) : 'unknown'
			});
		});
		
		// Listen for seek events using 'seeked' event (more reliable than timeupdate)
		audioEl.addEventListener('seeked', function() {
			showMediaActionFeedback();
		});
		
		// Track volume changes to save user's volume setting
		audioEl.addEventListener('volumechange', function() {
			if (audioEl.volume !== undefined && audioEl.volume >= 0) {
				// Update saved volume in player-core.js
				setSavedPlayerVolume(audioEl.volume);
			}
		});
		
		// Transcript is now handled via language links only
		
		// Load saved state when audio is ready
		audioEl.addEventListener('canplay', function onCanPlay() {
			audioEl.removeEventListener('canplay', onCanPlay);
			
			// Load saved state and restore position if available
			// Check if current track matches saved track
			const currentFile = getCurrentAudioFile();
			const savedState = loadSavedState();
			let shouldAutoPlay = false;
			
			if (savedState.track && currentFile && savedState.track.fileKey === currentFile.fileKey) {
				// Same track - restore position but don't autoplay
				if (savedState.position && savedState.position > 1) {
					audioEl.currentTime = savedState.position;
				}
				shouldAutoPlay = false; // Don't autoplay if restoring saved position
			} else {
				// New track - autoplay with fade-in
				shouldAutoPlay = true;
			}
			
			// Restore collapsed state if saved
			if (savedState.collapsed) {
				setAudioPlayerCollapsed(true);
				// Apply collapse animation if needed
				const audioPlayerElement = getAudioPlayerElement();
				if (audioPlayerElement && audioPlayerElement.length) {
					animatePlayerCollapse(true, null);
				}
			}
			
			// Auto-play new track with fade-in
			if (shouldAutoPlay) {
				// Get current volume from audio element (user's current volume setting)
				// This is the volume that user has set in the player controls
				const currentVolume = audioEl.volume !== undefined && audioEl.volume > 0 ? audioEl.volume : getSavedPlayerVolume();
				audioEl.play().then(() => {
					// Apply fade-in using current volume from element (user's volume setting)
					fadeInVolume(audioEl, currentVolume, 4000);
				}).catch(err => {
					console.warn('[AudioPlayer] Auto-play failed (user interaction may be required):', err);
				});
			}
			
			// Initialize state persistence after audio is ready
			initPlayerStatePersistence();
		}, { once: true });
		
		audio.on('loadedmetadata', function() {
			if (window.debugMode === '1') {
				console.log('[AudioPlayer] Audio metadata loaded:', {
					duration: this.duration,
					readyState: this.readyState,
					networkState: this.networkState
				});
			}
		});
		audio.on('error', function() {
			console.error('[AudioPlayer] Audio load error:', {
				error: this.error,
				errorCode: this.error ? this.error.code : null,
				errorMessage: this.error ? this.error.message : null,
				networkState: this.networkState,
				readyState: this.readyState,
				src: this.currentSrc || audioUrl
			});
		});
	}
}

